import BackHeader from 'modules/salon/pages/BackHeader';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import s from '../Vouchers.module.scss';
import { VoucherCard } from '../VoucherCard';
import { useQuery } from '@tanstack/react-query';
import { supabase } from 'sdk/api';
import useGetSalonById from 'sdk/hooks/salon/useGetSalonById';
import { format } from 'date-fns';
import Button from 'components/shared/Button/Button';
import { ReactComponent as ShareIcon } from 'images/new-icons/share-icon-yellow.svg';

const variants = ['pink', 'teal', 'yellow', 'purple', 'blue', 'green', 'orange', 'red'];

const getGiftCardById = async (id: string | undefined) => {
  if (!id) return null;
  const { data, error } = await supabase
    .from('gift_cards')
    .select(
      `
      *,
      services:service_type (
        name
      )
    `,
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  return data;
};

const createVoucherData = (
  denomination: number,
  serviceName: string,
  validUntil: string,
  color?: string,
) => ({
  value: denomination,
  validUntil: format(new Date(validUntil), 'dd.MM.yyyy'),
  validFor: serviceName,
  serviceName,
  color,
});

const BuyedVoucher = () => {
  const navigation = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: card } = useQuery({
    queryKey: ['giftCard', id],
    queryFn: () => getGiftCardById(id),
    enabled: !!id,
  });
  const { data: salon } = useGetSalonById(card?.salon_id);

  const voucherData = card
    ? {
        ...createVoucherData(
          card.denomination,
          card.services?.name ?? '',
          card.valid_until,
          card?.color,
        ),
        salonName: salon?.name ?? '',
        avatarSrc: salon?.avatar,
      }
    : null;
  return (
    <div className={s.bvoucher}>
      <BackHeader title="Gift Cards" onBack={() => navigation('/')} />
      <div className={s.bvoucher_title}>
        <h1>You've just unlocked a little joy.</h1>
        <p>This Beautify gift card is ready to send, save, or use right away.</p>
      </div>
      {voucherData && (
        <div className={s.bvoucher_card_wrapper}>
          <VoucherCard
            variant={
              variants.includes(card?.color?.toLowerCase())
                ? (card?.color?.toLowerCase() as
                    | 'pink'
                    | 'teal'
                    | 'yellow'
                    | 'purple'
                    | 'blue'
                    | 'green'
                    | 'orange'
                    | 'red')
                : 'pink'
            }
            {...voucherData}
          />
        </div>
      )}
      <div className={s.bvoucher_info_block}>
        <div className={s.info}>
          <div className={s.info_service}>
            <div className={s.info_service_header}>
              <img src={salon?.avatar} alt="" />
              <span className={s.info_service_header_title}>{salon?.name}</span>
            </div>
          </div>
          <div className={s.info_service_title}>{card?.services?.name ?? 'Gift Card'}</div>
        </div>
        <div className={s.contact_container}>
          <div className={s.contact_container_item + ' ' + s.contact_container_item_full}>
            <span className={s.contact_container_item_label}>Services</span>
            <span className={s.contact_container_item_value}>{card?.services?.name ?? '—'}</span>
          </div>
          <div className={s.contact_container_item}>
            <span className={s.contact_container_item_label}>Phone number</span>
            <span className={s.contact_container_item_value}>
              {salon?.phone ? (salon.phone.includes('+') ? salon.phone : `+${salon.phone}`) : '—'}
            </span>
          </div>
          <div className={s.contact_container_item}>
            <span className={s.contact_container_item_label}>Email</span>
            <span className={s.contact_container_item_value}>{salon?.email ?? '—'}</span>
          </div>
          <div className={s.contact_container_item + ' ' + s.contact_container_item_full}>
            <span className={s.contact_container_item_label}>Location</span>
            <span className={s.contact_container_item_value}>{salon?.full_address ?? '—'}</span>
          </div>
        </div>
      </div>
      <div className={s.bvoucher_actions}>
        <Button
          wide
          white
          className={s.bvoucher_btn_outline}
          onClick={() => navigation(card?.salon_id ? `/salon/${card.salon_id}` : '/shop')}
        >
          Continue shopping
        </Button>
        <Button
          wide
          darkGreen
          className={s.bvoucher_btn_share}
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: 'Gift Card',
                text: 'Check out this Beautify gift card!',
                url: window.location.href,
              });
            }
          }}
        >
          <ShareIcon className={s.bvoucher_share_icon} />
          Share Now!
        </Button>
      </div>
    </div>
  );
};

export default BuyedVoucher;
